import React, { useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Bevel,
  Button,
  RunningMan,
  Text,
  Window,
  colors,
  isDesktop,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import { toPlainText } from '@ogaim/core';
import { useStore } from '../store';
import { RichTextEditor, RichTextValue, emptyRichText, toBody } from '../components/RichTextEditor';
import { RichText } from '../components/RichText';

export interface AwayMessageScreenProps {
  onClose: () => void;
  frame?: boolean;
}

/** A couple of the canned messages AIM shipped with. */
const PRESETS = [
  'I am away from my computer right now.',
  'brb',
  'Out to lunch.',
  'On the phone.',
];

/**
 * The away-message editor: compose a rich-text message, preview it as buddies
 * will see it, and go away.
 */
export function AwayMessageScreen({ onClose, frame = true }: AwayMessageScreenProps) {
  const theme = useTheme();
  const styles = useStyles();
  const me = useStore((s) => s.me);
  const setPresence = useStore((s) => s.setPresence);
  const away = me?.presence === 'AWAY';

  const [value, setValue] = useState<RichTextValue>(() => ({
    // Seed from the current away message so editing it is not retyping it.
    text: me?.awayMessage ? toPlainText(me.awayMessage) : PRESETS[0]!,
    images: [],
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = toBody(value);
  const canSave = body.length > 0 && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      await setPresence('AWAY', body);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'The away message could not be saved.');
      setBusy(false);
    }
  };

  const comeBack = async () => {
    setBusy(true);
    setError(null);
    try {
      await setPresence('ONLINE', null);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Could not come back.');
      setBusy(false);
    }
  };

  const content = (
    <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
      <Text>Buddies who message you will see this until you come back.</Text>

      <RichTextEditor
        value={value}
        onChange={setValue}
        editable={!busy}
        height={92}
        autoFocus
        placeholder="Away message"
        accessibilityLabel="Away message"
      />

      <View style={styles.presets}>
        {PRESETS.map((p) => (
          <Button
            key={p}
            title={p.length > 22 ? `${p.slice(0, 21)}…` : p}
            onPress={() => setValue({ ...value, text: p })}
            disabled={busy}
            style={styles.preset}
          />
        ))}
      </View>

      <Text bold style={styles.previewLabel}>
        Preview
      </Text>
      <Bevel variant="sunken" background={theme.colors.fieldBg} contentStyle={styles.previewInner}>
        {body ? (
          <RichText body={body} />
        ) : (
          <Text disabled>Nothing yet.</Text>
        )}
      </Bevel>

      <View style={styles.statusWell}>
        {error ? <Text style={styles.error}>{error}</Text> : null}
      </View>

      <View style={styles.actions}>
        {away ? (
          <Button title="I'm Back" onPress={comeBack} disabled={busy} style={styles.action} />
        ) : (
          <Button title="Cancel" onPress={onClose} disabled={busy} style={styles.action} />
        )}
        <Button
          title={busy ? 'Saving…' : away ? 'Update' : 'Go Away'}
          onPress={save}
          disabled={!canSave}
          primary
          style={styles.action}
        />
      </View>
    </ScrollView>
  );

  if (!frame) return <View style={styles.frameless}>{content}</View>;

  return (
    <View style={styles.backdrop}>
      <Window
        title="Away Message"
        icon={<RunningMan size={14} />}
        onClose={busy ? undefined : onClose}
        style={styles.window}
      >
        {content}
      </Window>
    </View>
  );
}

const useStyles = makeStyles((t) => ({
  backdrop: { flex: 1, backgroundColor: t.colors.desktop, alignItems: 'center', justifyContent: 'center', padding: 12 },
  window: { width: '100%', maxWidth: 360, flex: isDesktop ? 0 : 1, maxHeight: 620 },
  frameless: { flex: 1, backgroundColor: t.colors.face, padding: 3 },
  body: { padding: 8, gap: 8 },
  presets: { flexDirection: 'row', flexWrap: 'wrap', gap: 3 },
  preset: { flexGrow: 1, minWidth: 0 },
  previewLabel: { marginTop: 2 },
  previewInner: { padding: 5, minHeight: 52 },
  statusWell: { minHeight: 16, justifyContent: 'center' },
  error: { color: '#800000' },
  actions: { flexDirection: 'row', gap: 6 },
  action: { flex: 1 },
}));
